import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import {
  DEFAULT_ORGANIZATION_USER_ROLE,
  ORGANIZATION_USER_ROLES,
  ORGANIZATION_USER_ROLE_LABELS,
  formatOrganizationUserRoleLabel,
  normalizeOrganizationUserRoleForSelect,
} from "../constants/organizationUserRoles.js";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  
  const [user, setUser] = React.useState(null);
  const [organizations, setOrganizations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingOrgs, setLoadingOrgs] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  // Add Organization Modal State
  const [addOrgModalOpen, setAddOrgModalOpen] = React.useState(false);
  const [allOrganizationsList, setAllOrganizationsList] = React.useState([]);
  const [loadingAllOrgs, setLoadingAllOrgs] = React.useState(false);
  const [orgModalSearchQuery, setOrgModalSearchQuery] = React.useState("");
  const [selectedOrgForAdd, setSelectedOrgForAdd] = React.useState(null);
  const [newOrganizationRole, setNewOrganizationRole] = React.useState(DEFAULT_ORGANIZATION_USER_ROLE);
  const [addingOrganization, setAddingOrganization] = React.useState(false);
  const [addOrgError, setAddOrgError] = React.useState(null);
  const [removingOrganization, setRemovingOrganization] = React.useState(null);
  
  // Tab state
  const [tabValue, setTabValue] = React.useState(0);
  
  // Edit form state
  const [editFirstName, setEditFirstName] = React.useState("");
  const [editLastName, setEditLastName] = React.useState("");
  const [editEmail, setEditEmail] = React.useState("");
  const [updatingUser, setUpdatingUser] = React.useState(false);
  const [updateUserError, setUpdateUserError] = React.useState(null);

  const fetchUser = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/admin/users/${id}`);
      setUser(data);
    } catch (err) {
      console.error("Error fetching user:", err);
      setError(err.message || "Failed to fetch user");
    } finally {
      setLoading(false);
    }
  }, [id, api]);

  const fetchOrganizations = React.useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const data = await api.get(`/admin/users/${id}/organizations`);
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching organizations:", err);
    } finally {
      setLoadingOrgs(false);
    }
  }, [id, api]);

  const fetchAllOrganizations = React.useCallback(async (searchQuery = "") => {
    setLoadingAllOrgs(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "20");
      if (searchQuery && searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }
      const allOrgsData = await api.get(`/admin/organizations?${params.toString()}`);
      setAllOrganizationsList(allOrgsData.data || []);
    } catch (err) {
      console.error("Error fetching all organizations:", err);
    } finally {
      setLoadingAllOrgs(false);
    }
  }, [api]);

  const handleOpenAddOrgModal = async () => {
    setAddOrgModalOpen(true);
    setOrgModalSearchQuery("");
    setSelectedOrgForAdd(null);
    setAddOrgError(null);
    await fetchAllOrganizations("");
  };

  const handleCloseAddOrgModal = () => {
    setAddOrgModalOpen(false);
    setOrgModalSearchQuery("");
    setSelectedOrgForAdd(null);
    setAddOrgError(null);
  };

  const handleSelectOrganization = (org) => {
    setSelectedOrgForAdd(org);
    setAddOrgError(null);
  };

  const handleAddSelectedOrganization = async () => {
    if (!user || !selectedOrgForAdd) return;

    setAddingOrganization(true);
    setAddOrgError(null);

    try {
      await api.post(`/admin/organizations/${selectedOrgForAdd.id}/users`, {
        user_id: user.id,
        role: newOrganizationRole,
        is_active: true,
      });

      // Refresh organizations and close modal
      fetchOrganizations();
      handleCloseAddOrgModal();
      setNewOrganizationRole(DEFAULT_ORGANIZATION_USER_ROLE);
    } catch (err) {
      console.error("Error adding organization:", err);
      setAddOrgError(err.message || "Failed to add user to organization");
    } finally {
      setAddingOrganization(false);
    }
  };

  const handleRemoveOrganization = async (organizationId) => {
    if (!user || !organizationId) return;

    if (!window.confirm("Are you sure you want to remove this user from the organization?")) {
      return;
    }

    setRemovingOrganization(organizationId);
    setError(null);

    try {
      await api.delete(`/admin/organizations/${organizationId}/users/${user.id}`);

      // Refresh organizations
      fetchOrganizations();
    } catch (err) {
      console.error("Error removing organization:", err);
      setError(err.message || "Failed to remove user from organization");
    } finally {
      setRemovingOrganization(null);
    }
  };
  
  const handleUpdateUser = async () => {
    if (!user) return;
    
    setUpdatingUser(true);
    setUpdateUserError(null);
    
    try {
      const updatedUser = await api.patch(`/admin/users/${user.id}`, {
        first_name: editFirstName.trim() || null,
        last_name: editLastName.trim() || null,
        email: editEmail.trim() || null,
      });
      
      // Update local user state
      setUser(updatedUser);
      
      // Show success message (you could add a success state if needed)
    } catch (err) {
      console.error("Error updating user:", err);
      setUpdateUserError(err.message || "Failed to update user");
    } finally {
      setUpdatingUser(false);
    }
  };

  // Debounce search
  React.useEffect(() => {
    if (!addOrgModalOpen) return;
    
    const timeoutId = setTimeout(() => {
      fetchAllOrganizations(orgModalSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [orgModalSearchQuery, addOrgModalOpen, fetchAllOrganizations]);

  React.useEffect(() => {
    fetchUser();
    fetchOrganizations();
  }, [id, fetchUser, fetchOrganizations]);
  
  // Initialize edit form when user data loads
  React.useEffect(() => {
    if (user) {
      setEditFirstName(user.first_name || "");
      setEditLastName(user.last_name || "");
      setEditEmail(user.email || "");
    }
  }, [user]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            </Container>
          </Box>
        </Box>
      </>
    );
  }

  if (error && !user) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Alert severity="error">{error}</Alert>
            </Container>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard/users")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Users
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {user?.name || user?.email || "User Details"}
                </Typography>
              </Breadcrumbs>
            </Box>
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={() => navigate("/dashboard/users")} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {user?.name || user?.email || "User Details"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View user details and organizations
                </Typography>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Edit User" />
                  <Tab label="Organizations" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                {updateUserError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {updateUserError}
                  </Alert>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={editFirstName}
                        onChange={(e) => {
                          setEditFirstName(e.target.value);
                          setUpdateUserError(null);
                        }}
                        placeholder="Enter first name"
                        disabled={updatingUser}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={editLastName}
                        onChange={(e) => {
                          setEditLastName(e.target.value);
                          setUpdateUserError(null);
                        }}
                        placeholder="Enter last name"
                        disabled={updatingUser}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={editEmail}
                        onChange={(e) => {
                          setEditEmail(e.target.value);
                          setUpdateUserError(null);
                        }}
                        placeholder="Enter email address"
                        disabled={updatingUser}
                        type="email"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        User ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} color="text.secondary" fontFamily="monospace">
                        {user?.id || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Auth0 User ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} color="text.secondary" fontFamily="monospace">
                        {user?.auth0_user_id || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {user?.created_at
                          ? new Date(user.created_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Updated
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {user?.updated_at
                          ? new Date(user.updated_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateUser}
                      disabled={
                        updatingUser ||
                        (editFirstName === (user?.first_name || "") &&
                         editLastName === (user?.last_name || "") &&
                         editEmail === (user?.email || ""))
                      }
                      startIcon={updatingUser ? <CircularProgress size={16} /> : <EditIcon />}
                    >
                      {updatingUser ? "Updating..." : "Update User"}
                    </Button>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Organizations ({organizations.length})
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleOpenAddOrgModal}
                    disabled={addingOrganization || loadingOrgs}
                    startIcon={<AddIcon />}
                    size="small"
                  >
                    Find and Add Organizations
                  </Button>
                </Box>
                
                {addOrgError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {addOrgError}
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                        <TableRow>
                          <TableCell>Organization Name</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Joined</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingOrgs ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={60} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={80} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={24} width={60} borderRadius={4} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : organizations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary">
                              This user is not associated with any organizations
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        organizations.map((org) => {
                          const membershipRole = normalizeOrganizationUserRoleForSelect(org.role);
                          return (
                          <TableRow key={org.id}>
                            <TableCell>{org.organization_name || "N/A"}</TableCell>
                            <TableCell>{org.physical_state || "N/A"}</TableCell>
                            <TableCell>
                              <Chip
                                label={formatOrganizationUserRoleLabel(org.role)}
                                size="small"
                                color={
                                  membershipRole === "admin"
                                    ? "secondary"
                                    : membershipRole === "editor"
                                      ? "primary"
                                      : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={org.is_active ? "Active" : "Inactive"}
                                color={org.is_active ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {org.joined_at
                                ? new Date(org.joined_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => navigate(`/dashboard/organizations/${org.id}`)}
                                >
                                  View
                                </Button>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveOrganization(org.id)}
                                  disabled={removingOrganization === org.id}
                                  title="Remove from organization"
                                >
                                  {removingOrganization === org.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Add Organization Modal */}
      <Dialog open={addOrgModalOpen} onClose={handleCloseAddOrgModal} maxWidth="md" fullWidth>
        <DialogTitle>Find and Add Organizations</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search organizations by name..."
              value={orgModalSearchQuery}
              onChange={(e) => setOrgModalSearchQuery(e.target.value)}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newOrganizationRole}
                onChange={(e) => setNewOrganizationRole(e.target.value)}
                label="Role"
                disabled={addingOrganization || loadingAllOrgs}
              >
                {ORGANIZATION_USER_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {ORGANIZATION_USER_ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {loadingAllOrgs ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Organization Name</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allOrganizationsList.map((org, index) => {
                    const isMember = organizations.some((uo) => uo.id === org.id);
                    const isSelected = selectedOrgForAdd?.id === org.id;
                    const isEven = index % 2 === 0;
                    return (
                      <TableRow
                        key={org.id}
                        onClick={() => !isMember && handleSelectOrganization(org)}
                        sx={{
                          cursor: isMember ? "not-allowed" : "pointer",
                          opacity: isMember ? 0.6 : 1,
                          bgcolor: isSelected 
                            ? "action.selected" 
                            : isEven 
                              ? "grey.50" 
                              : "background.paper",
                          "&:hover": isMember ? {} : { bgcolor: "action.hover" },
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={isSelected ? "bold" : "normal"}>
                            {org.organization_name || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>{org.physical_state || "N/A"}</TableCell>
                        <TableCell>
                          <Chip
                            label={org.is_active ? "Active" : "Inactive"}
                            color={org.is_active ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isMember ? (
                            <Chip label="Already Member" size="small" color="default" />
                          ) : isSelected ? (
                            <Chip label="Selected" size="small" color="primary" />
                          ) : (
                            <Button size="small" variant="outlined">
                              Select
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {allOrganizationsList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">
                          {orgModalSearchQuery ? "No organizations found matching your search" : "No organizations available"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {selectedOrgForAdd && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "primary.light", borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Selected Organization
              </Typography>
              <Typography>
                {selectedOrgForAdd.organization_name || selectedOrgForAdd.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role: {newOrganizationRole}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddOrgModal}>Cancel</Button>
          <Button
            onClick={handleAddSelectedOrganization}
            variant="contained"
            disabled={!selectedOrgForAdd || addingOrganization || loadingAllOrgs}
            startIcon={addingOrganization ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {addingOrganization ? "Adding..." : "Add Organization"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
